<template>
  <AuthLayout>
    <form @submit.prevent="handleSubmit" class="flex flex-col w-full gap-4">
      <InputPrimary :placeholder="$t('login.email')" v-model="email" :icon="EmailIcon" class="text-[#497384]" />
      <InputPrimary
        :placeholder="$t('login.password')"
        v-model="password"
        :icon="PassIcon"
        :type="showPassword ? 'text' : 'password'"
        class="w-full"
      >
        <button
          type="button"
          @click="showPassword = !showPassword"
          class="flex items-center justify-center ml-2 flex-shrink-0"
        >
          <img
            :src="showPassword ? EyeOffIcon : EyeIcon"
            alt="toggle password visibility"
            class="w-5 h-5"
          />
        </button>
      </InputPrimary>
      <ButtonComponent type="submit" :title="$t('login.submit')" class="bg-[#3E7C94]" />
      <router-link to="/forgot-password" class="self-center text-sm text-[#3E7C94] hover:underline">
        {{ $t('login.forgot_password') }}
      </router-link>
    </form>

    <div class="w-full border-t border-gray-200 pt-6 flex flex-col gap-5 text-sm text-[#385660]">
      <p class="flex items-start gap-3">
        <svg class="w-7 h-7 shrink-0 text-[var(--c-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
        </svg>
        <span>{{ $t('login.legacy_access_hint', { link: $t('login.forgot_password') }) }}</span>
      </p>
      <p class="flex items-start gap-3">
        <svg class="w-7 h-7 shrink-0 text-[var(--c-primary)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
        </svg>
        <span>{{ $t('login.bookmark_hint') }}</span>
      </p>
    </div>

    <!-- Modal: cuenta sin contraseña, debe restablecerla -->
    <div v-if="showPasswordResetModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b">
          <h3 class="text-lg font-semibold text-[#385660]">{{ $t('login.passwordResetRequired.title') }}</h3>
          <button @click="showPasswordResetModal = false" class="text-gray-400 hover:text-gray-600 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="p-6">
          <p class="text-gray-600">{{ $t('login.passwordResetRequired.message') }}</p>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t flex justify-end">
          <button @click="showPasswordResetModal = false"
            class="px-4 py-2 bg-[#3E7C94] text-white rounded-lg hover:bg-[#33677c] transition-colors">
            {{ $t('login.passwordResetRequired.close') }}
          </button>
        </div>
      </div>
    </div>
  </AuthLayout>
</template>

<script setup>
import { ref } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { useRoute, useRouter } from 'vue-router';
import InputPrimary from '@/components/inputs/input-primary.vue';
import EmailIcon from "@/assets/svg/EmailIcon.vue";
import PassIcon from "@/assets/svg/PassIcon.vue";
import ButtonComponent from '@/components/button/button-component.vue';
import AuthLayout from '@/components/layout/AuthLayout.vue';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

import EyeIcon from '@/assets/img/Eye.svg';
import EyeOffIcon from '@/assets/img/EyeOff.svg';

const email = ref('');
const password = ref('');
const showPassword = ref(false);
const showPasswordResetModal = ref(false);
const userStore = useUserStore();
const router = useRouter();
const route = useRoute();

// Destino al que volver tras identificarse: es lo que permite que el botón
// «VER SOLICITUD» de los emails lleve a la solicitud concreta.
const resolveRedirect = () => {
  const redirect = route.query.redirect;
  if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return '/';
  }
  return redirect;
};

const handleSubmit = async () => {
  try {
    await userStore.login(email.value, password.value);
    toast.success("¡Inicio de sesión exitoso!", {
      autoClose: 500,
      position: "top-right",
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      onClose: () => router.push(resolveRedirect())
    });
  } catch (error) {
    console.error('Error en el login:', error);
    // Cuenta sin contraseña: el correo de restablecimiento ya va de camino.
    if (error.code === 'password_reset_required') {
      showPasswordResetModal.value = true;
      return;
    }
    toast.error("Error al iniciar sesión. Por favor, verifica tus credenciales.", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }
};
</script>
